import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, ArrowRight, ChevronLeft, ChevronRight, Loader2, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useT, useLang } from "@/context/LangContext";
import { localizeProduct } from "@/lib/product";
import { displayPrice, LANG_CURRENCY } from "@/lib/currency";
import { ProductGallery } from "@/components/ProductGallery";

export default function FeaturedProducts() {
  const t = useT();
  const { lang, currency } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    api.get("/products")
      .then((r) => setItems(r.data.slice(0, 10)))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const buy = async (id) => {
    setBuying(id);
    try {
      const { data } = await api.post("/checkout/session", {
        product_id: id, quantity: 1, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch { setBuying(null); alert(t("products.err")); }
  };

  const scrollBy = (dir) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  if (!loading && items.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="home-featured-products">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="kicker flex items-center gap-2">
            <ShoppingBag size={14} /> {t("featured.kicker")}
          </p>
          <h2 className="text-4xl lg:text-5xl font-display font-black tracking-tighter mt-4 max-w-2xl">
            {t("featured.title")}
          </h2>
          <p className="text-[var(--ink-soft)] mt-3 max-w-lg">{t("featured.desc")}</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => scrollBy(-1)} className="w-10 h-10 rounded-full border border-black/10 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors flex items-center justify-center" data-testid="featured-prev">
            <ChevronLeft size={18} />
          </button>
          <button onClick={() => scrollBy(1)} className="w-10 h-10 rounded-full border border-black/10 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors flex items-center justify-center" data-testid="featured-next">
            <ChevronRight size={18} />
          </button>
          <Link to="/produits" className="btn-primary !py-2 !px-4 text-sm" data-testid="featured-see-all">
            {t("featured.see_all")} <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[var(--accent)]" /></div>
      ) : (
        <div
          ref={scrollerRef}
          className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth"
          style={{ scrollbarWidth: "thin" }}
        >
          {items.map((raw) => {
            const p = localizeProduct(raw, lang);
            const price = displayPrice(Number(p.price), p.currency || "EUR", lang, currency);
            return (
              <div
                key={p.id}
                className="card-flat overflow-hidden flex-shrink-0 w-[300px] snap-start flex flex-col group"
                data-testid={`featured-product-${p.id}`}
              >
                {(p.images?.length > 0 || p.image_url) && (
                  <Link to="/produits" className="aspect-[4/3] overflow-hidden bg-[var(--bg-soft)] block relative">
                    <ProductGallery images={p.images?.length ? p.images : [p.image_url]} alt={p.name} />
                    <div className="absolute top-2 left-2 flex flex-col gap-1 z-10 pointer-events-none">
                      {p.stock > 0 && (
                        <span className="text-[10px] font-bold uppercase tracking-widest bg-green-500 text-white px-2 py-0.5 rounded-full flex items-center gap-1 w-fit">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                          {t(p.stock < 10 ? "stock.low" : "stock.in")}
                        </span>
                      )}
                      {p.in_stock_senegal && (
                        <span className="text-[10px] font-bold uppercase tracking-widest bg-[var(--accent)] text-white px-2 py-0.5 rounded-full flex items-center gap-1 w-fit">
                          <MapPin size={10} /> {t("stock.senegal")}
                        </span>
                      )}
                    </div>
                  </Link>
                )}
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-[var(--secondary)] font-bold">{p.category}</p>
                  <h3 className="font-display text-lg font-bold mt-2 line-clamp-2">{p.name}</h3>
                  <p className="text-[var(--ink-soft)] text-xs mt-2 line-clamp-2 flex-1">{p.description}</p>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-black/5">
                    <p className="font-display text-xl font-black">{price.text}</p>
                    <button
                      onClick={() => buy(p.id)}
                      disabled={buying === p.id}
                      className="btn-primary !py-2 !px-3 text-xs"
                      data-testid={`featured-buy-${p.id}`}
                    >
                      {buying === p.id ? <Loader2 size={14} className="animate-spin" /> : <><ShoppingBag size={12} /> {t("products.buy")}</>}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-[var(--ink-soft)] mt-4 opacity-70">
        {currency} · {t("products.rate")}
      </p>
    </section>
  );
}
